import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { HttpHelperService } from './helper.service';
import { environment } from '../../environment/environment';
import { ApiResponse } from '../models/apiResponse';
import {
  GraphData,
  FirstTimeEngagementData,
  AllCoursesCompletionData,
  CombinedCompletionData,
  QuizPerformanceResponse,
  MAndEData,
  GetMonthlyAveragesResponse,
  MAndELearnerNumberAverage,
  MoodAnalyticsData,
  MonthlyMoodActivityData
} from '../models/studentProfileModels';

type Bot  = 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd' | 'kitsozazi' | 'kitsoai' | 'mpilo';
/** Path / programme: lwazi uses personal|business; lesedi, kitso, etc. use path1 | path2 where applicable. */
type Type = 'personal' | 'business' | 'path1' | 'path2';

@Injectable({
  providedIn: 'root'
})
export class SupabaseReportIntegrationsService {
  private apiUrl = `${environment.apiBaseUrl}/lms-student-profiles`;

  constructor(
    private http: HttpClient,
    private httpHelper: HttpHelperService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /**
   * Fetches profile property data for a given bot, key and date range.
   * @param bot  The bot identifier
   * @param key  The profile property key (e.g. 'LwaziAge', 'kitsoV2Gender')
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getProfileData(bot: Bot, key: string, startDate: string, endDate: string): Observable<GraphData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<GraphData>>(
      `${this.apiUrl}/${bot}/${key}`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'profile data')),
      catchError(error => this.handleError(error, 'fetching profile data'))
    );
  }

  /**
   * Fetches first-time engagement count for a bot within a date range.
   * @param bot  The bot identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getFirstTimeEngagementCount(bot: Bot, startDate: string, endDate: string): Observable<FirstTimeEngagementData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<FirstTimeEngagementData>>(
      `${this.apiUrl}/${bot}/first-time-engagement`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'first-time engagement count')),
      catchError(error => this.handleError(error, 'fetching first-time engagement count'))
    );
  }

  /**
   * Fetches all-courses completion stats (pre/final quiz based) for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getAllCoursesCompletionStats(bot: Bot, type: Type, startDate: string, endDate: string): Observable<AllCoursesCompletionData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<AllCoursesCompletionData>>(
      `${this.apiUrl}/${bot}/${type}/all-courses-completion`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'all-courses completion stats')),
      catchError(error => this.handleError(error, 'fetching all-courses completion stats'))
    );
  }

  /**
   * Fetches combined course and quiz completion stats for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getCompletionStats(bot: Bot, type: Type, startDate: string, endDate: string): Observable<CombinedCompletionData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<CombinedCompletionData[]>>(
      `${this.apiUrl}/${bot}/${type}/completion-stats`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'completion stats')),
      catchError(error => this.handleError(error, 'fetching completion stats'))
    );
  }

  /**
   * Fetches pre/post quiz performance for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getPrePostQuizStats(bot: Bot, type: Type, startDate: string, endDate: string): Observable<QuizPerformanceResponse[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<QuizPerformanceResponse[]>>(
      `${this.apiUrl}/${bot}/${type}/pre-post-quiz`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'pre-post quiz stats')),
      catchError(error => this.handleError(error, 'fetching pre-post quiz stats'))
    );
  }

  /**
   * Fetches M&E quiz question stats for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getMAndEStats(bot: Bot, type: Type, startDate: string, endDate: string): Observable<MAndEData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MAndEData[]>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-quiz`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e quiz stats')),
      catchError(error => this.handleError(error, 'fetching m-and-e quiz stats'))
    );
  }

  /**
   * Fetches M&E monthly averages and learner completion numbers for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param endDate End date in 'YYYY-MM-DD' format (start date derived from year)
   */
  getMAndENumbers(bot: Bot, type: Type, startDate: string, endDate: string): Observable<GetMonthlyAveragesResponse> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<GetMonthlyAveragesResponse>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-numbers`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e numbers')),
      catchError(error => this.handleError(error, 'fetching m-and-e learner numbers'))
    );
  }

  /**
   * Fetches M&E overall average score for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param endDate End date in 'YYYY-MM-DD' format (start date derived from year)
   */
  getMAndEAverage(bot: Bot, type: Type, startDate: string, endDate: string): Observable<MAndELearnerNumberAverage> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MAndELearnerNumberAverage>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-average`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e average')),
      catchError(error => this.handleError(error, 'fetching m-and-e average'))
    );
  }

  /**
   * Fetches mood-analytics (emotion breakdown + totals) for a bot and type.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getMoodAnalytics(bot: Bot, type: Type, startDate: string, endDate: string): Observable<MoodAnalyticsData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MoodAnalyticsData[]>>(
      `${this.apiUrl}/${bot}/${type}/mood-analytics`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'mood analytics')),
      catchError(error => this.handleError(error, 'fetching mood analytics'))
    );
  }

  /**
   * Fetches monthly mood-logging activity (per-month unique logger counts) for
   * a bot and type. Any months the API omits are rendered as zero-state on the
   * consumer side so a full 12-month axis can be shown.
   * @param bot  The bot identifier
   * @param type The path/type identifier
   * @param startDate  Start date in 'YYYY-MM-DD' format
   * @param endDate    End date in 'YYYY-MM-DD' format
   */
  getMonthlyMoodActivity(bot: Bot, type: Type, startDate: string, endDate: string): Observable<MonthlyMoodActivityData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MonthlyMoodActivityData[]>>(
      `${this.apiUrl}/${bot}/${type}/monthly-logging-activity`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'monthly logging activity')),
      catchError(error => this.handleError(error, 'fetching monthly logging activity'))
    );
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private processResponse<T>(response: ApiResponse<T>, context: string): T {
    if (!response.success || !response.data) {
      throw new Error(response.error || `Invalid response from server for ${context}`);
    }
    return response.data;
  }

  private handleError(error: HttpErrorResponse | any, context: string): Observable<never> {
    let errorMessage = `An error occurred while ${context}`;

    if (isPlatformBrowser(this.platformId)) {
      if (error.error instanceof ErrorEvent) {
        errorMessage = `Client/Network Error while ${context}: ${error.error.message}`;
        console.error('Client/Network Error:', error.error.message);
      } else if (error instanceof HttpErrorResponse) {
        const backendError = error.error?.error || error.message;
        errorMessage = `Error ${error.status} while ${context}: ${backendError}`;
        console.error(`Backend error: ${error.status}`, error.error);
      } else {
        errorMessage = `Browser error while ${context}: ${error.message || error}`;
        console.error('Unknown Browser Error:', error);
      }
    } else {
      if (error instanceof HttpErrorResponse) {
        const backendError = error.error?.error || error.message;
        errorMessage = `SSR Error ${error.status} while ${context}: ${backendError}`;
        console.error(`Backend error (Server): ${error.status}`, error.message);
      } else {
        errorMessage = `Server-side error while ${context}: ${error.message || error}`;
        console.error('Server-side Error:', error);
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}